import {
  axiosInstancePolicyHolder,
  axiosInstanceAzureBlob,
  axiosProfileHolder,
  axiosInstanceGuestPolicyHolder,
} from './wrapper/apiwrapper';
import { isHomeOfficeUser, getAccountDetailsTokenCookie } from '../utils';

export const NetworkManager = {
  getPolicyDetails: (policyNumber: string | null, token: string) => {
    return axiosInstancePolicyHolder.get(
      `policyholder/policies/${policyNumber}/policysummary`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },
  getPolicyLists: (token: string) => {
    return axiosInstancePolicyHolder.get(`policyholder/policies/policylist`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isHomeOfficeUser() && {
          'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
        }),
      },
    });
  },
  getLatestStatementsPDF: ({ token, policyNumber }) => {
    return axiosInstancePolicyHolder.get(
      `policyholder/policies/${policyNumber}/document?type=latestStatement`,
      {
        headers: {
          Authorization: `Bearer ${token?.accessToken}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },
  getTransferTransaction: ({ token, policyNumber, transactiontype }) => {
    return axiosInstancePolicyHolder.get(
      `policyholder/policies/${policyNumber}/transfer/${transactiontype}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },
  getReportMonthAndYear: ({
    token,
    policyNumber,
    month,
    year,
    investmentIds,
  }) => {
    const investmentIdsQuery = Array.isArray(investmentIds)
      ? investmentIds
          .map((id) => `investmentIds=${encodeURIComponent(id)}`)
          .join('&&')
      : investmentIds;
    const url = `policyholder/policies/${policyNumber}/report/unitvalues/bymonth/${month}/${year}?${investmentIdsQuery}`;
    return axiosInstancePolicyHolder.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isHomeOfficeUser() && {
          'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
        }),
      },
    });
  },

  getReportByDay: ({ token, policyNumber, day }) => {
    const url = `policyholder/policies/${policyNumber}/report/unitvalues/byday/${day}`;
    return axiosInstancePolicyHolder.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isHomeOfficeUser() && {
          'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
        }),
      },
    });
  },

  sendTransferTransaction: ({
    token,
    policyNumber,
    transactiontype,
    payload,
  }) => {
    return axiosInstancePolicyHolder.post(
      `policyholder/policies/${policyNumber}/transfer/${transactiontype}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },

  getPolicyRelationshipModal: () => {
    return axiosInstanceAzureBlob.get(
      '/aemcontent/policy-relationship-model-cf.json',
    );
  },
  getContactUsContent: () => {
    return axiosInstanceAzureBlob.get('/aemcontent/contactus.json');
  },
  getPolicyServiceRequest: () => {
    return axiosInstanceAzureBlob.get(
      '/aemcontent/policy-service-request-page-cf.json',
    );
  },
  getStrategiesModal: () => {
    return axiosInstanceAzureBlob.get(
      '/aemcontent/strategy-information-cf.json',
    );
  },
  getAEMNotification: () => {
    return axiosInstanceAzureBlob.get(
      '/aemcontent/ilfs-policyholder-portal.model.json',
    );
  },
  getAEMGenericNotification: () => {
    return axiosInstanceAzureBlob.get(
      '/aemcontent/generic-notification.model.json',
    );
  },
  getProfileData: (token) => {
    return axiosProfileHolder.get(`profile/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  updateProfileData: (data, token) => {
    return axiosProfileHolder.put(`profile/user/contactinfo`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  checkUserId: (token, newUserId) => {
    return axiosProfileHolder.get(`profile/user/available`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-oa-newLogonid': newUserId,
      },
    });
  },
  getInvestmentOptions: (policyNumber: string | null, token: string) => {
    return axiosInstancePolicyHolder.get(
      `policyholder/policies/${policyNumber}/investmentoptions`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },
  sendInvestmentOptionElection: ({ token, policyNumber, payload }) => {
    return axiosInstancePolicyHolder.post(
      `policyholder/policies/${policyNumber}/investmentoptions`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isHomeOfficeUser() && {
            'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
          }),
        },
      },
    );
  },
  getPaymentsEncryptedData: ({
    token,
    policyNumber,
    paymentTypes,
    loanAmount,
    loanInterestAmount,
  }) => {
    const paymentTypesQueryParam = Array.isArray(paymentTypes)
      ? paymentTypes.map((id) => `types=${id}`).join('&')
      : `types=${paymentTypes}`;
    let url = `policyholder/policies/${policyNumber}/payment?${paymentTypesQueryParam}`;
    if (loanAmount !== undefined && loanAmount !== null) {
      url += `&loanAmount=${loanAmount}`;
    }
    if (loanInterestAmount !== undefined && loanInterestAmount !== null) {
      url += `&loanInterestAmount=${loanInterestAmount}`;
    }
    return axiosInstancePolicyHolder.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isHomeOfficeUser() && {
          'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
        }),
      },
    });
  },
  getRecentTrxHistory: ({ token, policyNumber }) => {
    const url = `policyholder/policies/${policyNumber}/financialactivities`;
    return axiosInstancePolicyHolder.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isHomeOfficeUser() && {
          'x-oa-statetoken': `${getAccountDetailsTokenCookie()}`,
        }),
      },
    });
  },

  validateGuestPaymentLogin: ({ payload }) => {
    const url = `public/policyholder/policies/guest/payment-login`;
    return axiosInstanceGuestPolicyHolder.post(url, {
      ...payload,
    });
  },

  getGuestPaymentEncryptedData: ({
    guestToken,
    paymentTypes,
    loanAmount,
    loanInterestAmount,
  }) => {
    const paymentTypesQueryParam = Array.isArray(paymentTypes)
      ? paymentTypes.map((id) => `types=${id}`).join('&')
      : `types=${paymentTypes}`;
    let url = `public/policyholder/policies/guest/payment?${paymentTypesQueryParam}`;
    if (loanAmount !== undefined && loanAmount !== null) {
      url += `&loanAmount=${loanAmount}`;
    }
    if (loanInterestAmount !== undefined && loanInterestAmount !== null) {
      url += `&loanInterestAmount=${loanInterestAmount}`;
    }
    return axiosInstanceGuestPolicyHolder.get(url, {
      headers: {
        'x-oa-guestToken': `${guestToken}`,
      },
    });
  },
};
